import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';
import { api, unwrap, type Schemas } from '@/api/client';
import { Card, CardHead, Segmented, StatCard } from '@/design-system';
import { formatNumber } from '@/lib/format';
import { LoadError } from '@/shell/LoadError';
import type { Website } from '@/features/sites/api';
import s from './analytics.module.css';

type Analytics = Schemas['AnalyticsOut'];
type Period = '7' | '30' | '90';

export const analyticsQuery = (siteId: string, days: number) => ({
  queryKey: ['websites', siteId, 'analytics', days],
  queryFn: (): Promise<Analytics> =>
    unwrap(api.GET('/admin/websites/{website_id}/analytics', { params: { path: { website_id: siteId }, query: { days } } })),
});

const share = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);

/**
 * «Аналитика» (P8-A05, additive to the site workspace, DESIGN_SPEC §9): the published site's own first-party
 * traffic: views and visitors (daily unique, no cookies), the funnel view → CTA → cart → checkout, where
 * visitors come from (referrers, UTM), devices and pages. Built from the existing cards, KPI tiles and the
 * dashboard's bar chart.
 */
export function AnalyticsTab() {
  const site = useOutletContext<Website>();
  const { t } = useTranslation('sites');
  const { t: tc } = useTranslation('common');
  const [period, setPeriod] = useState<Period>('30');
  const q = useQuery(analyticsQuery(site.id, Number(period)));
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  const a = q.data;
  const max = Math.max(1, ...(a?.series ?? []).map((p) => p.views));
  const views = a?.totals.views ?? 0;
  const funnelTop = a?.funnel[0]?.visitors ?? 0;
  const devices = a?.devices ?? {};
  const deviceTotal = Object.values(devices).reduce((x, y) => x + y, 0);
  return (
    <div className={s.tab}>
      <div className={s.toolbar}>
        <span className={s.note}>{t('analytics.note')}</span>
        <Segmented
          label={t('analytics.period')}
          value={period}
          onChange={setPeriod}
          options={(['7', '30', '90'] as const).map((k) => ({ value: k, label: t(`analytics.days_${k}`) }))}
        />
      </div>
      <div className={s.kpis}>
        <StatCard label={t('analytics.views')} value={formatNumber(views)} sub={t('analytics.views_sub')} />
        <StatCard label={t('analytics.visitors')} value={formatNumber(a?.totals.visitors ?? 0)} sub={t('analytics.visitors_sub')} />
        <StatCard label={t('analytics.cta')} value={formatNumber(a?.totals.cta_click ?? 0)} sub={t('analytics.cta_sub', { share: share(a?.funnel[1]?.visitors ?? 0, funnelTop) })} subTone="accent" />
        <StatCard label={t('analytics.cart')} value={formatNumber(a?.totals.add_to_cart ?? 0)} sub={t('analytics.cart_sub', { count: a?.totals.checkout_start ?? 0 })} />
      </div>
      <div className={s.grid}>
        <Card texture="dots" className={s.chart}>
          <CardHead eyebrow={t('analytics.chart_eyebrow', { days: period })} title={t('analytics.chart')} />
          <div className={s.bars} role="list" aria-label={t('analytics.chart')}>
            {(a?.series ?? []).map((p, i, list) => (
              <div key={p.day} className={s.barCol} role="listitem" aria-label={t('analytics.bar', { day: p.day, count: p.views })}>
                <div className={s.track}>
                  <div className={i === list.length - 1 ? `${s.fill} ${s.fillLast}` : s.fill} style={{ height: `${Math.round((p.views / max) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          {a && !views && <span className={s.empty}>{t('analytics.empty')}</span>}
        </Card>
        <Card className={s.listCard}>
          <CardHead eyebrow={t('analytics.funnel_eyebrow')} title={t('analytics.funnel')} />
          {(a?.funnel ?? []).map((f) => (
            <div key={f.step} className={s.funnelRow}>
              <span className={s.funnelLabel}>{t(`analytics.step.${f.step as 'view'}`)}</span>
              <div className={s.funnelTrack}>
                <div className={s.funnelFill} style={{ width: `${share(f.visitors, funnelTop)}%` }} />
              </div>
              <span className={s.funnelValue}>{formatNumber(f.visitors)}</span>
            </div>
          ))}
        </Card>
      </div>
      <div className={s.grid3}>
        <Card className={s.listCard}>
          <CardHead eyebrow={t('analytics.sources_eyebrow')} title={t('analytics.referrers')} />
          <TopList rows={(a?.referrers ?? []).map((r) => ({ key: String(r.host ?? ''), label: r.host ? String(r.host) : t('analytics.direct'), views: Number(r.views) }))} total={views} empty={tc('dash')} />
        </Card>
        <Card className={s.listCard}>
          <CardHead eyebrow={t('analytics.sources_eyebrow')} title={t('analytics.utm')} />
          <TopList
            rows={(a?.utm ?? []).map((r) => ({ key: `${r.source}/${r.medium}/${r.campaign}`, label: [r.source, r.medium, r.campaign].filter(Boolean).join(' · '), views: Number(r.views) }))}
            total={views}
            empty={tc('dash')}
          />
        </Card>
        <Card className={s.listCard}>
          <CardHead eyebrow={t('analytics.audience_eyebrow')} title={t('analytics.devices')} />
          <TopList rows={(['mobile', 'tablet', 'desktop'] as const).map((d) => ({ key: d, label: t(`analytics.device.${d}`), views: devices[d] ?? 0 }))} total={deviceTotal} empty={tc('dash')} />
        </Card>
      </div>
      <Card className={s.listCard}>
        <CardHead eyebrow={t('analytics.audience_eyebrow')} title={t('analytics.pages')} />
        <TopList rows={(a?.pages ?? []).map((r) => ({ key: String(r.path), label: String(r.path), views: Number(r.views) }))} total={views} empty={tc('dash')} />
      </Card>
    </div>
  );
}

function TopList({ rows, total, empty }: { rows: Array<{ key: string; label: string; views: number }>; total: number; empty: string }) {
  if (!rows.length) return <span className={s.empty}>{empty}</span>;
  return (
    <div className={s.rows}>
      {rows.map((r) => (
        <div key={r.key} className={s.row}>
          <span className={s.rowLabel}>{r.label}</span>
          <span className={s.rowShare}>{share(r.views, total)}%</span>
          <span className={s.rowValue}>{formatNumber(r.views)}</span>
        </div>
      ))}
    </div>
  );
}
